import React, { useState } from 'react';
import { Button, Typography, Alert, Paper } from '@mui/material';
import { useDispatch } from 'react-redux';
import { addApplication } from '../redux/jobSlice';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { JobApplication } from '../redux/jobSlice';
import ConfirmImport from './ConfirmImport';
import { MAPBOX_ACCESS_TOKEN } from '../mapboxConfig';
import { Box } from '@mui/material'

const ImportApplications: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [message, setMessage] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [importedData, setImportedData] = useState<JobApplication[] | null>(null);
  const dispatch = useDispatch();


  const handleReset = () => {
    setImportedData(null);
    setMessage('');
    setError('');
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setFile(event.target.files[0]);
    }
  };

  // const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
  //   event.preventDefault();
  //   if (!file) {
  //     setError('Please select a file to upload');
  //     return;
  //   }

  //   const fileExtension = file!.name.split('.').pop()?.toLowerCase();
  //   let data: any[] = [];

  //   try {
  //     // File type validation
  //     if (fileExtension !== 'csv' && fileExtension !== 'xlsx') {
  //       throw new Error('Unsupported file format. Please upload a CSV or XLSX file.');
  //     }

  //     // File size validation (e.g., limit to 5MB)
  //     const maxSize = 5 * 1024 * 1024; // 5MB in bytes
  //     if (file!.size > maxSize) {
  //       throw new Error('File size exceeds the limit of 5MB. Please upload a smaller file.');
  //     }

  //     // Parse the file based on its type
  //     if (fileExtension === 'csv') {
  //       data = await parseCSV(file!);
  //     } else if (fileExtension === 'xlsx') {
  //       data = await parseXLSX(file!);
  //     }

  //     // Basic data validation
  //     if (!Array.isArray(data) || data.length === 0) {
  //       throw new Error('The file appears to be empty or in an invalid format.');
  //     }

  //     // Log the raw imported data
  //     console.log('Raw imported data:', data);

  //     // Process the data (this will be expanded in the next step)
  //     const processedData = processImportedData(data);
  //     setImportedData(processedData);

  //     setMessage('File imported successfully. Please review the data below.');
  //     setError('');
  //   } catch (err) {
  //     console.error('Error during file import:', err);
  //     setError(
  //       err instanceof Error
  //         ? err.message
  //         : 'An unexpected error occurred while uploading the file'
  //     );
  //     setMessage('');
  //   } finally {
  //     // Reset the file input
  //     if (event.target instanceof HTMLFormElement) {
  //       event.target.reset();
  //     }
  //   }
  // };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    try {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      let data: any[] = [];

      if (fileExtension === 'csv') {
        data = await parseCSV(file);
      } else if (fileExtension === 'xlsx') {
        data = await parseXLSX(file);
      } else {
        throw new Error('Unsupported file format. Please upload a CSV or XLSX file.');
      }

      console.log('Parsed data:', data);

      const processedData = await processImportedData(data);
      console.log('Processed data:', processedData);

      setImportedData(processedData);
      setMessage('File imported successfully. Please review the data below.');
      setError('');
    } catch (err) {
      console.error('Error during file import:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred while uploading the file');
      setMessage('');
    }
  };

  const handleConfirm = () => {
    if (importedData) {
      importedData.forEach(application => {
        dispatch(addApplication(application));
      });
      setImportedData(null);
      setMessage('All applications confirmed and added successfully');
    }
  };

  const parseCSV = (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        complete: (results) => {
          resolve(results.data);
        },
        error: (error) => {
          reject(error);
        },
        header: true,
      });
    });
  };

  const parseXLSX = (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = e.target?.result;
        try {
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(sheet);
          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = (error) => reject(error);
      reader.readAsBinaryString(file);
    });
  };

  const processImportedData = async (data: any[]): Promise<JobApplication[]> => {
    const fetchFullLocation = async (location: string) => {
      if (location.toLowerCase() === 'remote') {
        return 'Remote';
      }

      const endpoint = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(location)}.json`;
      const params = new URLSearchParams({
        access_token: MAPBOX_ACCESS_TOKEN || '',
        types: 'place',
        limit: '1',
      });

      const response = await fetch(`${endpoint}?${params}`);
      const data = await response.json();
      return data.features.length > 0 ? data.features[0].place_name : location;
    };

    return Promise.all(
      data.map(async (item: any) => {
        const location = item.Location || item.location || '';
        const fullLocation = location && !location.includes(',') ? await fetchFullLocation(location) : location;

        const application: JobApplication = {
          id: '', // This will be generated by the backend
          company: item.Company || item.company || '',
          position: item.Role || item.Position || item.position || '',
          location: fullLocation,
          salary: item.Salary || item.salary || '',
          salaryType: 'yearly', // Default to yearly, can be adjusted later
          hoursPerWeek: 40, // Default to 40 hours, can be adjusted later
          status: mapStatus(item.Stage || item.Status || item.status || 'Applied'),
          dateApplied: formatDate(item['Date Applied'] || item.dateApplied || new Date().toISOString()),
          link: item['Link to Job'] || item.link || '',
        };

        return validateApplication(application);
      })
    );
  };

  const mapStatus = (status: string): JobApplication['status'] => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('interview')) return 'Interviewing';
    if (statusLower.includes('hire')) return 'Hired';
    if (statusLower.includes('reject')) return 'Rejected';
    return 'Applied';
  };

  const formatDate = (date: string): string => {
    const parsedDate = date.includes('/') ? new Date(date) : new Date(date);
    return isNaN(parsedDate.getTime()) ? new Date().toISOString() : parsedDate.toISOString();
  };

  const validateApplication = (application: JobApplication): JobApplication => {
    if (!application.company) application.company = 'Unknown Company';
    if (!application.position) application.position = 'Unknown Position';
    if (!application.location) application.location = 'Unknown Location';

    // Remove currency symbol and commas from salary
    application.salary = application.salary.replace(/[$,]/g, '');

    // Ensure salary is a number or empty string
    application.salary = isNaN(Number(application.salary)) ? '' : application.salary;

    return application;
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        mb: 4,
        borderRadius: 2,
        background: 'linear-gradient(135deg, #ffffff 0%, #f0f4f8 100%)',
      }}
    >
      <Typography variant="h5" gutterBottom sx={{ color: '#3a7bd5', textAlign: 'center', mb: 3 }}>
        Import Job Applications
      </Typography>
      {message && (
        <Alert severity="success" sx={{ mt: 2, mb: 3 }}>
          {message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 2, mb: 3 }}>
          {error}
        </Alert>
      )}
      {!importedData ? (
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <input
                type="file"
                accept=".csv,.xlsx"
                onChange={handleFileChange}
                style={{ display: 'none' }}
                id="file-input"
              />
              <label htmlFor="file-input">
                <Button
                  component="span"
                  variant="contained"
                  sx={{
                    background: 'linear-gradient(to right, #3a7bd5, #00d2ff)',
                    color: 'white',
                    '&:hover': {
                      background: 'linear-gradient(to right, #3a7bd5, #3a7bd5)',
                    },
                  }}
                >
                  Choose File
                </Button>
              </label>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  background: 'linear-gradient(to right, #3a7bd5, #00d2ff)',
                  color: 'white',
                  '&:hover': {
                    background: 'linear-gradient(to right, #3a7bd5, #3a7bd5)',
                  },
                }}
              >
                Upload
              </Button>
            </Box>
            {file && (
              <Typography variant="body2" sx={{ color: '#666', textAlign: 'center', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                Selected file: {file.name}
              </Typography>
            )}
          </Box>
        </form>
      ) : (
        <ConfirmImport data={importedData} onConfirm={handleConfirm} onReset={handleReset} />
      )}
    </Paper>
  );
};

export default ImportApplications;
